/**
 * Demand card (TRUS-820): the shape the API returns and the pure logic that
 * decides what each line says. The backend has already applied small-cell
 * suppression and decided which lines exist (a committed line only appears
 * when a committed source has data). This file never invents a number: a
 * hidden or missing value becomes "fewer than N" or a reason, never 0.
 */

export type DemandLine =
  | { kind: 'modeled'; low: number | null; high: number | null; hidden: boolean }
  | { kind: 'modeled_unavailable'; reason?: string | null }
  | {
      kind: 'served';
      value: number | null;
      hidden: boolean;
      lower_bound: boolean;
      counties: string[];
      period?: string | null;
    }
  | { kind: 'committed'; per_month: number | null; hidden: boolean; sources: string[] }
  | { kind: 'expressed'; value: number | null; hidden: boolean; days: number };

export interface DemandCardData {
  service_type: string;
  label?: string | null;
  lines: DemandLine[];
  sources: { name: string; url?: string | null; as_of?: string | null }[];
  small_cell: number;
  weekly_cap?: number | null;
  zip_count: number;
}

export interface DemandPayload {
  zip_count: number;
  service_types: string[];
  cards: DemandCardData[];
  reason?: string | null;
  small_cell: number;
}

type T = (key: string, fallback: string, vars?: Record<string, unknown>) => string;

const fmt = (n: number) => n.toLocaleString();

/** One rendered line: a headline figure and a caption. */
export interface RenderedLine {
  key: string;
  figure: string;
  caption: string;
}

export function renderLine(line: DemandLine, smallCell: number, t: T): RenderedLine | null {
  const fewer = t('demandCard.fewerThan', 'Fewer than {{n}}', { n: smallCell });
  switch (line.kind) {
    case 'modeled':
      return {
        key: 'modeled',
        figure:
          line.hidden || line.low === null || line.high === null
            ? fewer
            : t('demandCard.range', '{{low}} to {{high}}', { low: fmt(line.low), high: fmt(line.high) }),
        caption: t(
          'demandCard.modeledCaption',
          'Medi-Cal members nearby estimated to have this need (a modeled range, not a promise of referrals)',
        ),
      };
    case 'modeled_unavailable':
      return {
        key: 'modeled_unavailable',
        figure: '—',
        caption:
          line.reason === 'parameters_unset'
            ? t('demandCard.modeledUnsetParams', 'No estimate yet: our model for this service is still missing sourced inputs')
            : line.reason === 'no_estimate_for_zips'
              ? t('demandCard.modeledNoZips', 'No estimate for these ZIP codes yet')
              : t('demandCard.modeledNone', 'No estimate yet for this area'),
      };
    case 'served':
      return {
        key: 'served',
        figure: line.hidden || line.value === null ? fewer : line.lower_bound ? t('demandCard.atLeast', 'At least {{n}}', { n: fmt(line.value) }) : fmt(line.value),
        caption: t('demandCard.servedCaption', 'served today by Medi-Cal plans in {{counties}} (county-wide, DHCS)', {
          counties: line.counties.join(', '),
        }),
      };
    case 'committed':
      return {
        key: 'committed',
        figure: line.hidden || line.per_month === null ? fewer : fmt(line.per_month),
        caption: t('demandCard.committedCaption', 'expected per month (committed) from {{sources}}', {
          sources: line.sources.join(', '),
        }),
      };
    case 'expressed':
      return {
        key: 'expressed',
        figure: line.hidden || line.value === null ? fewer : fmt(line.value),
        caption: t('demandCard.expressedCaption', 'asked us in the last {{days}} days', { days: line.days }),
      };
    default:
      return null;
  }
}

export function renderCard(card: DemandCardData, t: T): RenderedLine[] {
  return card.lines.map((l) => renderLine(l, card.small_cell, t)).filter((x): x is RenderedLine => x !== null);
}
